<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faDownload, faUpload, faCheckSquare, faSquare, faSpinner, faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
  import pkg from 'file-saver';
  import { selectedStudy } from '$stores/selectionStore';
  const { saveAs } = pkg;

  const isDev = import.meta.env.DEV;
  const DEV_GLUCOSE_LIMIT = 500_000;

  export let show = false;

  const dispatch = createEventDispatcher();
  const API_BASE_URL = '/api/proxy';

  // All available collections
  const COLLECTIONS = [
    { name: 'Device', label: 'Device' },
    { name: 'ReadingMetric', label: 'Reading Metric' },
    { name: 'DeviceToReadingMetric', label: 'Device to Reading Metric' },
    { name: 'Language', label: 'Language' },
    { name: 'LookupGroup', label: 'Lookup Group' },
    { name: 'LookupType', label: 'Lookup Type' },
    { name: 'LocalizationEntries', label: 'Localization Entries' },
    { name: 'LocalizationTranslations', label: 'Localization Translations' },
    { name: 'SchedulerJobConfig', label: 'Scheduler Job Config' },
    { name: 'RegulatoryData', label: 'Regulatory Data' },
    { name: 'Study', label: 'Study' },
    { name: 'StudyProperties', label: 'Study Properties' },
    { name: 'Site', label: 'Site' },
    { name: 'Subject', label: 'Subject' },
    { name: 'GlucoseReading', label: 'Glucose Reading' }
  ];

  let activeTab = 'export'; // 'export', 'import', 'cleanup'
  let selectedCollections = COLLECTIONS.reduce((acc, col) => {
    acc[col.name] = false;
    return acc;
  }, {});
  let loading = false;
  let error = null;
  let success = null;
  let exportPreview = null;
  let preflightLoading = false;
  let exportJobId = null;
  let exportJobStatus = null;
  let exportPollTimer = null;
  let fileInput;
  let importResults = null;
  let selectedFile = null;
  let cleanupLoading = false;
  let previewLoading = false;
  let cleanupError = null;
  let cleanupSuccess = null;
  let cleanupPreview = null;
  let cleanupConfirmText = '';
  let previewStudyId = null;
  let cleanupCompleted = false;
  const cleanupConfirmInputId = 'cleanup-confirm-input';

  $: currentStudyName = $selectedStudy?.studyName || '';
  $: currentStudyProtocol = $selectedStudy?.protocolNumber || '';
  $: modalTitle = currentStudyName
    ? `Data Management - ${currentStudyName}`
    : 'Data Management';

  function initializeSelections() {
    const newSelections = {};
    COLLECTIONS.forEach(col => {
      newSelections[col.name] = false;
    });
    selectedCollections = newSelections;
  }

  function stopExportPolling() {
    if (exportPollTimer) {
      clearInterval(exportPollTimer);
      exportPollTimer = null;
    }
  }

  function handleClose() {
    stopExportPolling();
    show = false;
    error = null;
    success = null;
    exportPreview = null;
    preflightLoading = false;
    exportJobId = null;
    exportJobStatus = null;
    importResults = null;
    selectedFile = null;
    activeTab = 'export';
    cleanupLoading = false;
    previewLoading = false;
    cleanupError = null;
    cleanupSuccess = null;
    cleanupPreview = null;
    cleanupConfirmText = '';
    previewStudyId = null;
    cleanupCompleted = false;
    initializeSelections();
    if (fileInput) {
      fileInput.value = '';
    }
    dispatch('close');
  }

  function toggleCollection(collectionName) {
    selectedCollections = {
      ...selectedCollections,
      [collectionName]: !selectedCollections[collectionName]
    };
  }

  function selectAll() {
    COLLECTIONS.forEach(col => {
      selectedCollections[col.name] = true;
    });
    selectedCollections = { ...selectedCollections };
  }

  function deselectAll() {
    COLLECTIONS.forEach(col => {
      selectedCollections[col.name] = false;
    });
    selectedCollections = { ...selectedCollections };
  }

  // Make this reactive so the export button updates
  $: selectedCollectionNames = Object.keys(selectedCollections).filter(name => selectedCollections[name]);

  const WARN_THRESHOLD = 100_000;
  const DANGER_THRESHOLD = 1_000_000;

  function formatCount(n) {
    return n.toLocaleString();
  }

  $: devGuardBlocked = isDev
    && exportPreview
    && exportPreview.counts?.GlucoseReading >= DEV_GLUCOSE_LIMIT;

  function cancelExportPreview() {
    exportPreview = null;
    exportJobId = null;
    exportJobStatus = null;
    stopExportPolling();
    error = null;
  }

  async function handleExportPreflight() {
    if (selectedCollectionNames.length === 0) {
      error = 'Please select at least one collection to export';
      return;
    }

    preflightLoading = true;
    error = null;
    success = null;
    exportPreview = null;

    try {
      const response = await fetch(`${API_BASE_URL}/data-management/export/preflight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collections: selectedCollectionNames, studyGUID: $selectedStudy?._id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Preflight check failed');
      }

      const result = await response.json();
      if (!result.success) throw new Error('Preflight check failed');

      exportPreview = result;
    } catch (err) {
      console.error('Error in export preflight:', err);
      error = err.message || 'Failed to check collection sizes';
    } finally {
      preflightLoading = false;
    }
  }

  async function confirmExport() {
    loading = true;
    error = null;
    success = null;

    try {
      const response = await fetch(`${API_BASE_URL}/data-management/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collections: selectedCollectionNames,
          protocol: currentStudyProtocol,
          studyGUID: $selectedStudy?._id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start export');
      }

      const result = await response.json();
      if (!result.success || !result.jobId) throw new Error('Failed to start export');

      exportJobId = result.jobId;
      exportJobStatus = {
        status: 'processing',
        progress: { currentCollection: '', completedCollections: 0, totalCollections: selectedCollectionNames.length }
      };

      startExportPolling(result.jobId);
    } catch (err) {
      console.error('Error starting export:', err);
      error = err.message || 'Failed to start export';
    } finally {
      loading = false;
    }
  }

  function startExportPolling(jobId) {
    stopExportPolling();
    exportPollTimer = setInterval(() => pollExportStatus(jobId), 3000);
  }

  async function pollExportStatus(jobId) {
    try {
      const response = await fetch(`${API_BASE_URL}/data-management/export/status/${jobId}`);
      if (!response.ok) return;

      const result = await response.json();
      if (!result.success) return;

      exportJobStatus = result;

      if (result.status === 'completed') {
        stopExportPolling();
        success = 'Export complete. Click the button to download.';
      } else if (result.status === 'failed') {
        stopExportPolling();
        error = result.error || 'Export failed';
      }
    } catch (err) {
      console.error('Error polling export status:', err);
    }
  }

  async function handleImport() {
    if (!selectedFile) {
      error = 'Please select a file to import';
      return;
    }

    const file = selectedFile;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      error = 'File size exceeds 10MB limit';
      return;
    }

    loading = true;
    error = null;
    success = null;
    importResults = null;

    try {
      // Read and parse the file
      const fileContent = await file.text();
      let importData;

      try {
        importData = JSON.parse(fileContent);
      } catch (parseError) {
        throw new Error('Invalid JSON file format');
      }

      // Validate structure
      if (!importData.collections || typeof importData.collections !== 'object') {
        throw new Error('Invalid file structure: missing collections object');
      }

      // Send to API
      const response = await fetch(`${API_BASE_URL}/data-management/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collections: importData.collections })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Import failed');
      }

      const result = await response.json();

      if (result.success) {
        importResults = result.results;
        success = `Import completed: ${result.totals.inserted} inserted, ${result.totals.skipped} skipped`;
        
        // Clear file input
        selectedFile = null;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        throw new Error('Import failed');
      }
    } catch (err) {
      console.error('Error importing collections:', err);
      error = err.message || 'Failed to import collections';
    } finally {
      loading = false;
    }
  }

  function openFileDialog() {
    fileInput?.click();
  }

  function handleFileSelect(event) {
    selectedFile = event.target.files?.[0] || null;
    error = null;
    success = null;
    importResults = null;
  }

  async function fetchCleanupPreview() {
    if (!$selectedStudy?._id) {
      cleanupPreview = null;
      return;
    }

    previewLoading = true;
    cleanupError = null;
    cleanupSuccess = null;
    cleanupCompleted = false;
    previewStudyId = $selectedStudy._id;

    try {
      const response = await fetch(`${API_BASE_URL}/data-management/cleanup/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studyGUID: $selectedStudy._id })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Preview failed');
      }

      cleanupPreview = result;
    } catch (err) {
      console.error('Error fetching cleanup preview:', err);
      cleanupError = err.message || 'Failed to load preview';
      cleanupPreview = null;
    } finally {
      previewLoading = false;
    }
  }

  async function handleCleanup() {
    if (!$selectedStudy?._id) {
      cleanupError = 'Please select a study before running cleanup.';
      return;
    }

    if (cleanupConfirmText.trim() !== currentStudyName) {
      cleanupError = 'Confirmation text does not match the study name.';
      return;
    }

    cleanupLoading = true;
    cleanupError = null;
    cleanupSuccess = null;

    try {
      const response = await fetch(`${API_BASE_URL}/data-management/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studyGUID: $selectedStudy._id })
      });

      if (!response.ok) {
        let errorMessage = 'Cleanup failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (_) {
          // ignore parse error
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const filenameHeader = response.headers.get('Content-Disposition');
      const filenameMatch = filenameHeader?.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] || `study-cleanup-${new Date().toISOString().slice(0, 10)}.json`;
      saveAs(blob, filename);

      cleanupSuccess = 'Backup downloaded and cleanup completed.';
      cleanupError = null;
      cleanupCompleted = true;
      cleanupConfirmText = '';
      cleanupPreview = null;
      previewStudyId = null;
    } catch (err) {
      console.error('Error cleaning up study data:', err);
      cleanupError = err.message || 'Failed to cleanup study data';
    } finally {
      cleanupLoading = false;
    }
  }

  $: if (activeTab === 'cleanup' && $selectedStudy?._id && !cleanupCompleted) {
    if (previewStudyId !== $selectedStudy._id && !previewLoading) {
      fetchCleanupPreview();
    }
  }
</script>

{#if show}
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    transition:fade={{ duration: 200 }}
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {modalTitle}
        </h2>
        <button
          type="button"
          on:click={handleClose}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faXmark} class="h-6 w-6" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-gray-200 dark:border-gray-700">
        <button
          type="button"
          class="flex-1 px-4 py-3 text-sm font-medium {activeTab === 'export'
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}"
          on:click={() => { activeTab = 'export'; error = null; success = null; exportPreview = null; exportJobId = null; exportJobStatus = null; stopExportPolling(); }}
        >
          <FontAwesomeIcon icon={faDownload} class="mr-2" />
          Export
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-3 text-sm font-medium {activeTab === 'import'
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}"
          on:click={() => { activeTab = 'import'; error = null; success = null; }}
        >
          <FontAwesomeIcon icon={faUpload} class="mr-2" />
          Import
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-3 text-sm font-medium {activeTab === 'cleanup'
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}"
          on:click={() => { activeTab = 'cleanup'; error = null; success = null; cleanupError = null; cleanupSuccess = null; }}
        >
          Cleanup
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
        {#if activeTab === 'export'}
          <!-- Export Tab -->
          <div class="space-y-4">
            {#if !exportPreview}
              <!-- Step 1: Collection selection -->
              <div class="flex justify-between items-center mb-4">
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Select collections to export
                </p>
                <div class="space-x-2">
                  <button
                    type="button"
                    on:click={selectAll}
                    class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Select All
                  </button>
                  <span class="text-gray-400">|</span>
                  <button
                    type="button"
                    on:click={deselectAll}
                    class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                {#each COLLECTIONS as collection (collection.name)}
                  <div
                    role="button"
                    tabindex="0"
                    on:click={() => toggleCollection(collection.name)}
                    on:keydown={(e) => e.key === 'Enter' && toggleCollection(collection.name)}
                    class="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    {#key selectedCollections[collection.name]}
                      <div class="text-blue-600 dark:text-blue-400">
                        <FontAwesomeIcon
                          icon={selectedCollections[collection.name] ? faCheckSquare : faSquare}
                          class="h-5 w-5"
                        />
                      </div>
                    {/key}
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                      {collection.label}
                    </span>
                  </div>
                {/each}
              </div>
            {:else if exportJobId && exportJobStatus}
              <!-- Step 3: Export in progress / complete -->
              {#if exportJobStatus.status === 'processing'}
                <div class="flex flex-col items-center py-8 space-y-4">
                  <FontAwesomeIcon icon={faSpinner} class="h-8 w-8 text-blue-500 animate-spin" />
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Exporting to S3...
                  </p>
                  {#if exportJobStatus.progress}
                    <div class="w-full space-y-2">
                      <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{exportJobStatus.progress.currentCollection || 'Starting...'}</span>
                        <span>{exportJobStatus.progress.completedCollections} / {exportJobStatus.progress.totalCollections}</span>
                      </div>
                      <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          class="bg-blue-500 h-2 rounded-full transition-all duration-500"
                          style="width: {exportJobStatus.progress.totalCollections ? (exportJobStatus.progress.completedCollections / exportJobStatus.progress.totalCollections * 100) : 0}%"
                        ></div>
                      </div>
                    </div>
                  {/if}
                </div>
              {:else if exportJobStatus.status === 'completed' && exportJobStatus.downloadUrl}
                <div class="flex flex-col items-center py-8 space-y-4">
                  <div class="text-green-500">
                    <FontAwesomeIcon icon={faDownload} class="h-8 w-8" />
                  </div>
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Export complete
                  </p>
                  <a
                    href={exportJobStatus.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    Download ZIP from S3
                  </a>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    This link expires in 24 hours.
                  </p>
                </div>
              {/if}
            {:else}
              <!-- Step 2: Review counts before exporting -->
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Review document counts before exporting:
              </p>

              <div class="space-y-1.5">
                {#each Object.entries(exportPreview.counts) as [name, count]}
                  {@const label = COLLECTIONS.find(c => c.name === name)?.label || name}
                  <div class="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <span class="text-gray-700 dark:text-gray-300">{label}</span>
                    <span class="font-mono text-gray-600 dark:text-gray-400 {count >= DANGER_THRESHOLD ? 'text-red-600 dark:text-red-400 font-semibold' : count >= WARN_THRESHOLD ? 'text-yellow-600 dark:text-yellow-400 font-semibold' : ''}">
                      {formatCount(count)}
                    </span>
                  </div>
                {/each}
              </div>

              <div class="flex justify-between items-center p-2 border-t border-gray-200 dark:border-gray-600 mt-2 text-sm font-medium">
                <span class="text-gray-900 dark:text-gray-100">Total documents</span>
                <span class="font-mono text-gray-900 dark:text-gray-100">{formatCount(exportPreview.totalDocuments)}</span>
              </div>

              {#if devGuardBlocked}
                <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                  <strong>Blocked:</strong> GlucoseReading contains over {formatCount(DEV_GLUCOSE_LIMIT)} documents.
                  Large exports should be run in a deployed environment, not locally.
                </div>
              {:else if exportPreview.totalDocuments >= WARN_THRESHOLD}
                <div class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-700 dark:text-yellow-300">
                  This is a large export. It may take some time to complete.
                </div>
              {/if}
            {/if}

            <!-- Messages -->
            {#if error}
              <div class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            {/if}

            {#if success}
              <div class="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-600 dark:text-green-400">
                {success}
              </div>
            {/if}
          </div>
        {:else if activeTab === 'import'}
          <!-- Import Tab -->
          <div class="space-y-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload a JSON file to import collections. Documents with existing IDs will be skipped.
            </p>

            <input
              type="file"
              accept=".json"
              bind:this={fileInput}
              class="hidden"
              on:change={handleFileSelect}
            />

            <button
              type="button"
              on:click={openFileDialog}
              class="w-full px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
            >
              <div class="text-center">
                <FontAwesomeIcon icon={faUpload} class="h-8 w-8 text-gray-400 mb-2" />
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {selectedFile?.name || 'Click to select JSON file'}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Maximum file size: 10MB
                </p>
              </div>
            </button>

            <!-- Messages -->
            {#if error}
              <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            {/if}

            {#if success}
              <div class="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-600 dark:text-green-400">
                {success}
              </div>
            {/if}

            <!-- Import Results -->
            {#if importResults}
              <div class="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Import Results
                </h3>
                <div class="space-y-2 text-xs">
                  {#each Object.entries(importResults) as [collectionName, result]}
                    <div class="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded">
                      <span class="font-medium text-gray-700 dark:text-gray-300">
                        {collectionName}
                      </span>
                      {#if result.success}
                        <span class="text-gray-600 dark:text-gray-400">
                          {result.inserted} inserted, {result.skipped} skipped
                        </span>
                      {:else}
                        <span class="text-red-600 dark:text-red-400">
                          Error: {result.error}
                        </span>
                      {/if}
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        {:else}
          <!-- Cleanup Tab -->
          <div class="space-y-4">
            {#if !$selectedStudy?._id}
              <div class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-700 dark:text-yellow-300">
                Select a study to preview and run cleanup.
              </div>
            {:else}
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2">
                <div class="text-sm text-gray-700 dark:text-gray-200">
                  <span class="font-medium">Study:</span> {currentStudyName}
                </div>
                {#if currentStudyProtocol}
                  <div class="text-sm text-gray-700 dark:text-gray-200">
                    <span class="font-medium">Protocol:</span> {currentStudyProtocol}
                  </div>
                {/if}
              </div>

              <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                This will permanently delete all study-related data after downloading a backup.
              </div>

              <div class="space-y-2">
                <div class="text-sm text-gray-700 dark:text-gray-300">
                  Preview of data to be deleted:
                </div>
                {#if previewLoading}
                  <div class="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
                    Loading preview...
                  </div>
                {:else if cleanupPreview}
                  <div class="text-sm text-gray-700 dark:text-gray-300">
                    Found {cleanupPreview.counts?.Site || 0} sites, {cleanupPreview.counts?.Subject || 0} participants,
                    {cleanupPreview.counts?.GlucoseReading || 0} readings, {cleanupPreview.counts?.Message || 0} messages,
                    {cleanupPreview.counts?.AuditLog || 0} audit logs.
                  </div>
                {:else}
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Preview unavailable.
                  </div>
                {/if}
              </div>

              <div class="space-y-2">
                <label class="text-sm text-gray-700 dark:text-gray-300" for={cleanupConfirmInputId}>
                  Type the study name to confirm:
                </label>
                <input
                  type="text"
                  id={cleanupConfirmInputId}
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  bind:value={cleanupConfirmText}
                  placeholder={currentStudyName}
                />
              </div>
            {/if}

            {#if cleanupError}
              <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                {cleanupError}
              </div>
            {/if}

            {#if cleanupSuccess}
              <div class="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-600 dark:text-green-400">
                {cleanupSuccess}
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Footer -->
      <div class="flex justify-end items-center gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          on:click={handleClose}
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          disabled={loading}
        >
          Close
        </button>
        
        {#if activeTab === 'export'}
          {#if exportJobId && exportJobStatus}
            <!-- Step 3: job running or complete — no action buttons, just Close -->
          {:else if exportPreview}
            <button
              type="button"
              on:click={cancelExportPreview}
              disabled={loading}
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Back
            </button>
            <button
              type="button"
              on:click={confirmExport}
              disabled={loading || devGuardBlocked}
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded flex items-center gap-2"
            >
              {#if loading}
                <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
              {:else}
                <FontAwesomeIcon icon={faDownload} />
              {/if}
              Proceed with Export
            </button>
          {:else}
            <button
              type="button"
              on:click={handleExportPreflight}
              disabled={preflightLoading || selectedCollectionNames.length === 0}
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded flex items-center gap-2"
            >
              {#if preflightLoading}
                <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
              {:else}
                <FontAwesomeIcon icon={faDownload} />
              {/if}
              Export
            </button>
          {/if}
        {:else if activeTab === 'import'}
          <button
            type="button"
            on:click={handleImport}
            disabled={loading || !selectedFile}
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded flex items-center gap-2"
          >
            {#if loading}
              <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
            {:else}
              <FontAwesomeIcon icon={faUpload} />
            {/if}
            Import
          </button>
        {:else}
          <button
            type="button"
            on:click={handleCleanup}
            disabled={cleanupLoading || !$selectedStudy?._id || cleanupConfirmText.trim() !== currentStudyName}
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded flex items-center gap-2"
          >
            {#if cleanupLoading}
              <FontAwesomeIcon icon={faSpinner} class="animate-spin" />
            {:else}
              <FontAwesomeIcon icon={faDownload} />
            {/if}
            Backup & Delete
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}

